/**
 * Image Assets Library (/assets)
 *
 * Per-workspace browse view for the client image asset pool: page header with
 * total count, a left filter rail, a center grid/list of asset cards, and a
 * mounted detail drawer. Workspace-scoped via useTenantStore().currentTenant;
 * all data flows through useClientImageAssetsList (no direct Supabase access).
 *
 * State owned here:
 *   - filters: ClientImageAssetListParams + view (passed to the rail + hook)
 *   - selectedAssetId + drawerOpen (passed to AssetDetailDrawer)
 *
 * Clicking a card sets the selected asset id and opens the drawer. Pagination
 * advances offset by ±limit, bounded by `total` from the list response.
 */

import { useEffect, useMemo, useState } from "react"
import { ChevronLeft, ChevronRight, ImageIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useWorkspaceStore } from "@/stores/useWorkspaceStore"
import { useClientImageAssetsList } from "@/hooks/useClientImageAssetsList"
import {
  AssetFilterRail,
  type AssetFilterValue,
} from "@/components/image-assets/AssetFilterRail"
import { AssetGrid } from "@/components/image-assets/AssetGrid"
import { AssetDetailDrawer } from "@/components/image-assets/AssetDetailDrawer"
import { COPY } from "@/lib/copyTokens"

const DEFAULT_LIMIT = 24

const INITIAL_FILTERS: AssetFilterValue = {
  limit: DEFAULT_LIMIT,
  offset: 0,
}

function toListParams(filters: AssetFilterValue) {
  return filters
}

function GridSkeleton() {
  return (
    <div data-testid="image-assets-grid-skeleton" className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="overflow-hidden rounded-lg border border-border">
          <Skeleton className="aspect-video w-full" />
          <div className="space-y-2 p-3">
            <Skeleton className="h-4 w-3/4" />
            <div className="flex gap-1.5">
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-5 w-14 rounded-full" />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function NoWorkspace() {
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-2 text-center">
      <ImageIcon className="h-10 w-10 text-muted-foreground" />
      <h2 className="text-lg font-semibold text-foreground">{COPY.mediaAssets.noWorkspaceTitle}</h2>
      <p className="max-w-sm text-sm text-muted-foreground">
        {COPY.mediaAssets.noWorkspaceDescription}
      </p>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-card text-center">
      <ImageIcon className="h-10 w-10 text-muted-foreground" />
      <h2 className="text-lg font-semibold text-foreground">{COPY.mediaAssets.emptyTitle}</h2>
      <p className="max-w-sm text-sm text-muted-foreground">
        {COPY.mediaAssets.emptyDescription}
      </p>
    </div>
  )
}

interface ImageAssetsLibraryPageProps {
  /** External filter jump (e.g. the Settings Drive-sync receipt's
   * "Review N new →" CTA): every nonce change replaces the filters with a
   * fresh set + the requested state. */
  filterJump?: { state: string; nonce: number } | null
}

export default function ImageAssetsLibraryPage({
  filterJump = null,
}: ImageAssetsLibraryPageProps = {}) {
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace)
  const tenantId = currentWorkspace?.website ?? null

  const [filters, setFilters] = useState<AssetFilterValue>(INITIAL_FILTERS)
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Apply an external filter jump during render (derived-state pattern —
  // avoids a cascading setState-in-effect); each nonce is applied once.
  const [appliedJumpNonce, setAppliedJumpNonce] = useState<number | null>(null)
  if (filterJump && filterJump.nonce !== appliedJumpNonce) {
    setAppliedJumpNonce(filterJump.nonce)
    setFilters({ ...INITIAL_FILTERS, state: filterJump.state })
  }

  const listParams = useMemo(() => toListParams(filters), [filters])

  const { data, isLoading, isError } = useClientImageAssetsList(
    tenantId,
    listParams
  )

  useEffect(() => {
    setFilters(INITIAL_FILTERS)
    setSelectedAssetId(null)
    setDrawerOpen(false)
  }, [tenantId])

  const handleFilterChange = (patch: Partial<AssetFilterValue>) => {
    setFilters((prev) => ({ ...prev, ...patch }))
  }

  const handleSelect = (assetId: string) => {
    setSelectedAssetId(assetId)
    setDrawerOpen(true)
  }

  const limit = filters.limit ?? DEFAULT_LIMIT
  const offset = filters.offset ?? 0
  const total = data?.total ?? 0
  const items = data?.items ?? []
  const hasPrev = offset > 0
  const hasNext = offset + limit < total
  const rangeStart = total === 0 ? 0 : offset + 1
  const rangeEnd = Math.min(offset + limit, total)

  const goPrev = () =>
    setFilters((prev) => ({
      ...prev,
      offset: Math.max(0, (prev.offset ?? 0) - (prev.limit ?? DEFAULT_LIMIT)),
    }))
  const goNext = () =>
    setFilters((prev) => ({
      ...prev,
      offset: (prev.offset ?? 0) + (prev.limit ?? DEFAULT_LIMIT),
    }))

  if (!tenantId) {
    return (
      <div className="space-y-6">
        <NoWorkspace />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <section className="space-y-4" aria-label={COPY.mediaAssets.filterRegion}>
        <AssetFilterRail value={filters} onChange={handleFilterChange} />
        <div className="space-y-4">
          <p data-testid="image-assets-result-count" className="text-sm text-ink-2">
            {isLoading ? COPY.mediaAssets.loading : COPY.mediaAssets.resultCount(total)}
          </p>
          {isLoading ? (
            <GridSkeleton />
          ) : isError ? (
            <div className="flex min-h-[40vh] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-destructive/30 bg-destructive/5 text-center">
              <ImageIcon className="h-10 w-10 text-destructive" />
              <h2 className="text-lg font-semibold text-foreground">
                {COPY.mediaAssets.errorTitle}
              </h2>
              <p className="max-w-sm text-sm text-muted-foreground">
                {COPY.mediaAssets.errorDescription}
              </p>
            </div>
          ) : total === 0 ? (
            <EmptyState />
          ) : (
            <>
              <AssetGrid
                items={items}
                onSelect={handleSelect}
                className="grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4"
              />

              {/* Pagination */}
              <div className="flex items-center justify-between border-t border-border pt-4">
                <p className="text-sm text-muted-foreground">
                  {COPY.mediaAssets.showingRange(rangeStart, rangeEnd, total)}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={goPrev}
                    disabled={!hasPrev}
                  >
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    {COPY.mediaAssets.previous}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={goNext}
                    disabled={!hasNext}
                  >
                    {COPY.mediaAssets.next}
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Detail drawer (always mounted; visibility controlled by `open`) */}
      <AssetDetailDrawer
        tenantId={tenantId}
        assetId={selectedAssetId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  )
}
